import SEO from '../components/SEO.jsx';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { 
  FaCode, 
  FaMobile, 
  FaPaintBrush, 
  FaShoppingCart, 
  FaRobot, 
  FaArrowRight,
  FaAndroid,
  FaApple,
  FaGoogle,
  FaFacebook,
  FaHeadset,
  FaTools
} from 'react-icons/fa';
import { SiFlutter } from 'react-icons/si';
import Stats from '../components/sections/Stats.jsx';
import ServiceModal from '../components/ServiceModal.jsx';

const Services = () => {
  const [ref, inView] = useInView({ 
    threshold: 0, // Trigger as soon as any part is visible
    triggerOnce: true,
    rootMargin: '200px 0px -100px 0px' // Trigger 200px before entering, stop 100px before leaving
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  const openModal = (serviceName) => {
    setSelectedService(serviceName);
    setIsModalOpen(true);
  };

  const servicesStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "IT Solutions",
    "provider": {
      "@type": "Organization",
      "name": "Sutrasync",
      "url": "https://sutrasync.com"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "IT Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Website Design & Development",
            "description": "Custom, responsive websites that drive results"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Mobile App Development",
            "description": "iOS and Android mobile applications"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Digital Marketing",
            "description": "SEO, social media, and digital advertising"
          }
        }
      ]
    }
  };

  const services = [
    {
      icon: FaCode,
      title: 'Website Design & Development',
      description: 'Custom, responsive websites that drive results and provide exceptional user experiences across all devices.',
      subServices: [
        { name: 'Responsive Web Design', icon: FaCode },
        { name: 'Custom Development', icon: FaCode },
        { name: 'SEO Optimization', icon: FaCode },
        { name: 'Performance Optimization', icon: FaCode },
        { name: 'Content Management Systems', icon: FaCode },
        { name: 'E-commerce Integration', icon: FaCode }
      ],
      technologies: ['NEXT.JS', 'REACT', 'NODE.JS', 'MONGODB', 'TYPESCRIPT', 'GRAPHQL'],
      delay: 0
    },
    {
      icon: FaMobile,
      title: 'Mobile Experience',
      description: 'Native and cross-platform mobile applications that engage users and deliver seamless functionality.',
      subServices: [
        { name: 'iOS App Development', icon: FaApple },
        { name: 'Android App Development', icon: FaAndroid },
        { name: 'Cross-Platform Solutions', icon: SiFlutter },
        { name: 'UI/UX Design for Mobile', icon: FaPaintBrush },
        { name: 'App Store Optimization', icon: FaMobile },
        { name: 'Mobile App Maintenance', icon: FaTools }
      ],
      technologies: ['REACT NATIVE', 'FLUTTER', 'SWIFT', 'KOTLIN', 'XAMARIN'],
      delay: 0.1
    },
    {
      icon: FaShoppingCart,
      title: 'E-commerce Solutions',
      description: 'Powerful e-commerce solutions that drive sales and provide exceptional shopping experiences.',
      subServices: [
        { name: 'Online Store Development', icon: FaShoppingCart },
        { name: 'Payment Gateway Integration', icon: FaShoppingCart },
        { name: 'Inventory Management', icon: FaShoppingCart },
        { name: 'Order Management System', icon: FaShoppingCart },
        { name: 'Multi-vendor Platforms', icon: FaShoppingCart },
        { name: 'Mobile Commerce', icon: FaMobile }
      ],
      technologies: ['NEXT.JS COMMERCE', 'REACT', 'NODE.JS', 'STRIPE', 'MONGODB', 'HEADLESS CMS'],
      delay: 0.2
    },
    {
      icon: FaRobot,
      title: 'AI & Machine Learning',
      description: 'Cutting-edge AI solutions to automate processes and provide intelligent business insights.',
      subServices: [
        { name: 'Machine Learning Models', icon: FaRobot },
        { name: 'Natural Language Processing', icon: FaRobot },
        { name: 'Computer Vision', icon: FaRobot },
        { name: 'Predictive Analytics', icon: FaRobot },
        { name: 'Chatbots & Virtual Assistants', icon: FaRobot },
        { name: 'Data Analysis & Insights', icon: FaRobot }
      ],
      technologies: ['PYTHON', 'TENSORFLOW', 'PYTORCH', 'OPENAI GPT', 'LANGCHAIN', 'AWS AI'],
      delay: 0.3
    },
    {
      icon: FaGoogle,
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing strategies to boost your online presence and drive conversions.',
      subServices: [
        { name: 'Search Engine Optimization', icon: FaGoogle },
        { name: 'Pay-Per-Click Advertising', icon: FaGoogle },
        { name: 'Social Media Marketing', icon: FaFacebook },
        { name: 'Content Marketing', icon: FaGoogle },
        { name: 'Email Marketing', icon: FaGoogle },
        { name: 'Analytics & Reporting', icon: FaGoogle }
      ],
      technologies: ['GOOGLE ADS', 'META ADS', 'GOOGLE ANALYTICS 4', 'SEMRUSH', 'HUBSPOT', 'MAILCHIMP'],
      delay: 0.4
    },
    {
      icon: FaPaintBrush,
      title: 'UI/UX Design',
      description: 'Intuitive and visually appealing designs that enhance user satisfaction and business growth.',
      subServices: [
        { name: 'User Experience Research', icon: FaPaintBrush },
        { name: 'Wireframing & Prototyping', icon: FaPaintBrush },
        { name: 'Visual Design', icon: FaPaintBrush },
        { name: 'Design Systems', icon: FaPaintBrush },
        { name: 'Usability Testing', icon: FaPaintBrush },
        { name: 'Brand Identity Design', icon: FaPaintBrush }
      ],
      technologies: ['FIGMA', 'ADOBE XD', 'SKETCH', 'INVISION', 'PRINCIPLE'],
      delay: 0.5
    },
    {
      icon: FaHeadset,
      title: 'Customer Care Support',
      description: '24/7 customer support services to ensure your clients receive the best possible experience.',
      subServices: [
        { name: '24/7 Live Support', icon: FaHeadset },
        { name: 'Multi-channel Support', icon: FaHeadset },
        { name: 'Help Desk Solutions', icon: FaHeadset },
        { name: 'Knowledge Base Creation', icon: FaHeadset },
        { name: 'Support Ticket Management', icon: FaHeadset },
        { name: 'Customer Satisfaction Tracking', icon: FaHeadset }
      ],
      technologies: ['ZENDESK', 'FRESHDESK', 'INTERCOM', 'LIVECHAT', 'CUSTOM SOLUTIONS'],
      delay: 0.6
    },
    {
      icon: FaTools,
      title: 'Support & Maintenance',
      description: 'Ongoing support and maintenance services to keep your digital assets running smoothly.',
      subServices: [
        { name: 'Regular Updates & Patches', icon: FaTools },
        { name: 'Security Monitoring', icon: FaTools },
        { name: 'Performance Optimization', icon: FaTools },
        { name: 'Bug Fixes & Troubleshooting', icon: FaTools },
        { name: 'Backup & Recovery', icon: FaTools },
        { name: 'Technical Support', icon: FaTools }
      ],
      technologies: ['MONITORING TOOLS', 'SECURITY SOFTWARE', 'BACKUP SOLUTIONS', 'DEVOPS TOOLS'],
      delay: 0.7
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06, // Even faster stagger
        delayChildren: 0 // No initial delay
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 }, // Minimal distance for instant feel
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4, // Quick animation
        ease: [0.25, 0.1, 0.25, 1] // Smooth easing
      }
    }
  };

  return (
    <>
      <SEO 
        title="Premium IT Services: Web, App & Digital Marketing | Sutrasync"
        description="Comprehensive IT solutions: Modern web development, native & hybrid mobile apps, SEO, social media marketing, UI/UX design, e-commerce. Latest tech stack, affordable pricing, guaranteed results."
        keywords="Sutrasync services, web development services, responsive web design, mobile app development, android app development, iOS app development, React Native apps, Flutter apps, digital marketing services, SEO services, social media marketing, UI UX design, e-commerce development, custom software development, Karnataka, India"
        url="https://sutrasync.com/services"
        image="https://sutrasync.com/vr-banner.png"
        structuredData={servicesStructuredData}
      />

      <div className="pt-16 md:pt-20 font-opensans">
        {/* Page Header Banner - Blue Background with Background Image */}
        <section className="relative bg-[#22446b] py-20 overflow-hidden min-h-[500px] flex items-center">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src="/vr-banner.png" 
              alt="Services Background" 
              className="w-full h-full object-cover opacity-25"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#22446b]/80 via-[#106bcb]/70 to-[#22446b]/80"></div>
          </div>

          {/* Geometric Line Pattern Overlay */}
          <div className="absolute inset-0 opacity-8">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="geometric-lines" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
                  {/* Main diagonal lines */}
                  <path d="M0 0 L120 120" stroke="white" strokeWidth="0.8" opacity="0.4"/>
                  <path d="M120 0 L0 120" stroke="white" strokeWidth="0.8" opacity="0.4"/>
                  {/* Grid lines */}
                  <path d="M60 0 L60 120" stroke="white" strokeWidth="0.6" opacity="0.3"/>
                  <path d="M0 60 L120 60" stroke="white" strokeWidth="0.6" opacity="0.3"/>
                  {/* Secondary lines */}
                  <path d="M30 0 L30 120" stroke="white" strokeWidth="0.4" opacity="0.2"/>
                  <path d="M90 0 L90 120" stroke="white" strokeWidth="0.4" opacity="0.2"/>
                  <path d="M0 30 L120 30" stroke="white" strokeWidth="0.4" opacity="0.2"/>
                  <path d="M0 90 L120 90" stroke="white" strokeWidth="0.4" opacity="0.2"/>
                  {/* Corner accents */}
                  <circle cx="30" cy="30" r="2" fill="white" opacity="0.3"/>
                  <circle cx="90" cy="90" r="2" fill="white" opacity="0.3"/>
                  <circle cx="90" cy="30" r="2" fill="white" opacity="0.3"/>
                  <circle cx="30" cy="90" r="2" fill="white" opacity="0.3"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#geometric-lines)" />
            </svg>
          </div>

          {/* Content Container */}
          <div className="container mx-auto px-6 lg:px-8 relative z-10 text-center">
            {/* Page Title */}
            <motion.h1 
              className="text-5xl lg:text-7xl font-rajdhani font-bold text-white mb-6 tracking-tight"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              Services
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              className="text-xl lg:text-2xl text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            >
              Comprehensive IT solutions designed to accelerate business growth and drive digital transformation
            </motion.p>

            {/* Breadcrumb Navigation */}
            <motion.div 
              className="flex items-center justify-center gap-3 text-white/70 text-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <a href="/" className="hover:text-white transition-colors flex items-center gap-2 group">
                <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
                Home
              </a>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-white font-medium">Services</span>
            </motion.div>

            {/* Decorative Elements */}
            <motion.div 
              className="absolute top-1/2 left-10 transform -translate-y-1/2 hidden lg:block"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
            >
              <div className="w-20 h-20 border-2 border-white/20 rounded-full flex items-center justify-center">
                <div className="w-10 h-10 border border-white/30 rounded-full"></div>
              </div>
            </motion.div>

            <motion.div 
              className="absolute top-1/2 right-10 transform -translate-y-1/2 hidden lg:block"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.8 }}
            >
              <div className="w-16 h-16 border border-white/20 rotate-45">
                <div className="w-full h-full border border-white/30 rotate-45"></div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Wave Decoration */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg className="w-full h-16" viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path d="M0,60 C300,120 900,0 1200,60 L1200,120 L0,120 Z" fill="white" opacity="0.1"/>
            </svg>
          </div>
        </section>

        {/* Support Team Section */}
        <section className="section-padding bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="order-2 lg:order-1"
              >
                <h2 className="text-3xl lg:text-4xl xl:text-5xl font-rajdhani font-bold text-[#22446b] mb-6 leading-tight">
                  Your Complete IT Support Partner - Extending Your Technology Capabilities
                </h2>
                
                <p className="text-gray-600 text-base lg:text-lg leading-relaxed mb-5 font-opensans">
                  Whether you have a few computers or offices throughout the country, we provide expert assistance when equipment fails, networks crash, or you simply need a helping hand. Our flexible maintenance contracts fit various pricing and service-level requirements.
                </p>
                
                <p className="text-gray-600 text-base lg:text-lg leading-relaxed mb-6 font-opensans">
                  Our managed IT solutions give companies of all sizes access to technology specialists who excel in network management, cybersecurity, and technical support.
                </p>
              </motion.div>

              {/* Right Content - Image */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="order-1 lg:order-2 flex justify-center lg:justify-end"
              >
                <div className="relative w-full max-w-lg">
                  {/* Main Image */}
                  <img 
                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80" 
                    alt="IT Support Team Collaboration" 
                    className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                  />
                  
                  {/* Decorative Elements */}
                  <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#338edc]/5 rounded-2xl -z-10 hidden lg:block"></div>
                  <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#338edc]/5 rounded-full -z-10 hidden lg:block"></div>
                  
                  {/* Stats Badge */}
                  <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4 hidden sm:block">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-2xl font-rajdhani font-bold text-gray-900">24/7</p>
                        <p className="text-xs text-gray-600 font-opensans">Support Available</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Featured Services Section - White Background with Black Pattern */}
        <section className="section-padding relative overflow-hidden bg-white">
          {/* Modern Geometric Pattern Background */}
          <div className="absolute inset-0 opacity-5">
            <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="modern-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                  <rect x="0" y="0" width="30" height="30" fill="none" stroke="black" strokeWidth="0.5"/>
                  <rect x="30" y="30" width="30" height="30" fill="none" stroke="black" strokeWidth="0.5"/>
                  <circle cx="15" cy="15" r="2" fill="black" opacity="0.3"/>
                  <circle cx="45" cy="45" r="2" fill="black" opacity="0.3"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#modern-pattern)" />
            </svg>
          </div>

          <div className="container mx-auto px-6 lg:px-8 relative z-10">
            {/* Section Header - Centered */}
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
            >
              {/* Modern Line Icons + Title - Centered */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-12 h-0.5 bg-[#106bcb]"></div>
                <span className="text-[#22446b] text-sm font-semibold uppercase tracking-wider">Featured Service</span>
                <div className="w-12 h-0.5 bg-[#106bcb]"></div>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-rajdhani font-bold text-[#22446b] leading-tight max-w-4xl mx-auto">
                Advanced Solutions for Growing Your Online Business
              </h2>

              {/* Modern CTA Button - Centered */}
              <motion.div
                className="mt-8"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button 
                  onClick={() => openModal('General Inquiry')}
                  className="inline-flex items-center bg-[#106bcb] text-white font-rajdhani font-bold py-3 px-8 rounded-none transition-all duration-300 shadow-lg hover:shadow-xl group border-2 border-[#106bcb] hover:bg-white hover:text-[#106bcb]"
                >
                  Get Started
                  <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
              </motion.div>
            </motion.div>

            {/* Services Grid - Professional Cards like Image */}
            <div className="max-w-7xl mx-auto">
              <motion.div 
                ref={ref}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                variants={containerVariants}
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
              >
                {services.slice(0, 8).map((service, index) => (
                  <motion.div 
                    key={index}
                    className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100"
                    variants={itemVariants}
                    whileHover={{ 
                      y: -8,
                      transition: { duration: 0.3 }
                    }}
                  >
                    {/* Left Border Accent - Shows on Hover */}
                    <div className="absolute left-0 top-8 bottom-8 w-1 bg-transparent group-hover:bg-gradient-to-b group-hover:from-[#106bcb] group-hover:to-[#22446b] rounded-r-full transition-all duration-300"></div>

                    {/* Header Section */}
                    <div className="flex items-start gap-4 mb-8">
                      {/* Icon Container */}
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-[#106bcb] rounded-2xl flex items-center justify-center group-hover:bg-[#22446b] transition-colors duration-300 shadow-lg">
                          <service.icon className="text-2xl text-white" />
                        </div>
                      </div>

                      {/* Title */}
                      <div className="flex-1">
                        <h3 className="text-xl font-rajdhani font-bold text-[#22446b] leading-tight">
                          {service.title}
                        </h3>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed text-sm mb-8 font-opensans">
                      {service.description}
                    </p>

                    {/* Features Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      {service.subServices.slice(0, 6).map((subService, subIndex) => (
                        <div 
                          key={subIndex}
                          className="flex items-center gap-3 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                        >
                          <div className="w-2 h-2 bg-[#106bcb] rounded-full flex-shrink-0"></div>
                          <span className="text-sm font-medium">
                            {subService.name}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Technologies Section */}
                    <div className="mb-8">
                      <div className="flex flex-wrap gap-2">
                        {service.technologies && service.technologies.map((tech, techIndex) => (
                          <span 
                            key={techIndex}
                            className="px-3 py-1 bg-gray-100 text-[#22446b] text-xs font-semibold rounded-full hover:bg-gray-200 transition-colors duration-200"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="pt-4 border-t border-gray-100">
                      <button 
                        onClick={() => openModal(service.title)}
                        className="w-full flex items-center justify-center gap-2 bg-[#106bcb] text-white font-rajdhani font-bold py-3 px-6 rounded-xl hover:bg-[#22446b] transition-all duration-300 group/btn shadow-lg hover:shadow-xl"
                      >
                        START PROJECT
                        <FaArrowRight className="text-sm group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>

                    {/* Hover Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-gray-50/30 opacity-0 group-hover:opacity-100 rounded-3xl transition-opacity duration-500 pointer-events-none" />
                    
                    {/* Corner Decoration */}
                    <div className="absolute top-4 right-4 w-8 h-8 border-2 border-gray-200 rounded-full flex items-center justify-center group-hover:border-gray-400 transition-colors duration-300">
                      <div className="w-2 h-2 bg-gray-300 rounded-full group-hover:bg-gray-500 transition-colors duration-300"></div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <Stats />
      </div>

      {/* Service Modal */}
      <ServiceModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        serviceName={selectedService}
      />
    </>
  );
};

export default Services;